import React, { FormEvent, useMemo, useRef, useState } from 'react';
import { BuildingIcon, CheckCircle2Icon, ClockIcon, FileSignatureIcon, FileTextIcon, LayoutGridIcon, ListIcon, LoaderCircleIcon, MailIcon, PhoneIcon, PlusIcon, SearchIcon, SendHorizontalIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { askCarli, AgentMessage } from '../utils/carliAgent';
type CompanyType = 'Brand' | '3PL partner' | 'Strategic partner' | 'Portfolio company';
type Stage = 'Lead' | 'Qualified' | 'Contract sent' | 'Signed' | 'Activated';
type ContractStatus = 'None' | 'Sent' | 'Signed';
type Company = {
  id: string;
  name: string;
  domain: string;
  type: CompanyType;
  stage: Stage;
  owner: string;
  contract: ContractStatus;
  value: string;
  lastActivity: string;
  location: string;
};
type Activity = {
  id: string;
  company: string;
  kind: 'Email' | 'Contract' | 'Call' | 'Note';
  summary: string;
  owner: string;
  timestamp: string;
};
type ChatEntry = {
  id: number;
  author: 'carli' | 'user';
  text: string;
  citation?: string;
};
const stages: Stage[] = ['Lead', 'Qualified', 'Contract sent', 'Signed', 'Activated'];
const companyTypes: CompanyType[] = ['Brand', '3PL partner', 'Strategic partner', 'Portfolio company'];
const stageTone: Record<Stage, string> = {
  Lead: 'bg-ink/10 text-ink/60',
  Qualified: 'bg-[#4D9CC7]/15 text-[#2F6E8F]',
  'Contract sent': 'bg-[#C9A04E]/18 text-[#8B6A20]',
  Signed: 'bg-[#BB5E40]/12 text-[#94452E]',
  Activated: 'bg-forest/12 text-forest'
};
const typeTone: Record<CompanyType, string> = {
  Brand: 'bg-[#BB5E40]/10 text-[#94452E]',
  '3PL partner': 'bg-[#4D9CC7]/12 text-[#2F6E8F]',
  'Strategic partner': 'bg-[#A77ACA]/14 text-[#6B4A8C]',
  'Portfolio company': 'bg-forest/10 text-forest'
};
export const initialCompanies: Company[] = [{
  id: 'eddy',
  name: 'Eddy',
  domain: 'eddy.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$210,600',
  lastActivity: '12m',
  location: 'New York, US'
}, {
  id: 'one-people',
  name: '1 People',
  domain: '1people.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$96,400',
  lastActivity: '1h',
  location: 'Copenhagen, DK'
}, {
  id: 'nature-baby',
  name: 'Nature Baby',
  domain: 'naturebaby.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$68,200',
  lastActivity: '3h',
  location: 'Auckland, NZ'
}, {
  id: 'matdalee',
  name: 'MATDALEE Studio',
  domain: 'matdalee.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$41,800',
  lastActivity: 'Yesterday',
  location: 'Los Angeles, US'
}, {
  id: 'seaesta',
  name: 'SEAESTA SURF',
  domain: 'seaestasurf.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meg Sawtelle',
  contract: 'Sent',
  value: '$52,000',
  lastActivity: '2d',
  location: 'San Diego, US'
}, {
  id: 'lili-blanc',
  name: 'Lili Blanc',
  domain: 'liliblanc.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meris Butler',
  contract: 'Sent',
  value: '$34,500',
  lastActivity: '2d',
  location: 'Dubai, AE'
}, {
  id: 'hermoza',
  name: 'Hermoza',
  domain: 'hermoza.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$28,900',
  lastActivity: '4d',
  location: 'Washington, US'
}, {
  id: 'no-more-nobody',
  name: 'No More Nobody',
  domain: 'nomorenobody.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$22,400',
  lastActivity: '5d',
  location: 'London, UK'
}, {
  id: 'arkitaip',
  name: 'arkitaip',
  domain: 'arkitaip.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$18,000',
  lastActivity: '1w',
  location: 'London, UK'
}, {
  id: 'il-gufo',
  name: 'Il Gufo',
  domain: 'ilgufo.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$46,000',
  lastActivity: '1w',
  location: 'Asolo, IT'
}, {
  id: 'shipbob',
  name: 'ShipBob',
  domain: 'shipbob.com',
  type: '3PL partner',
  stage: 'Activated',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '—',
  lastActivity: '3d',
  location: 'Chicago, US'
}, {
  id: 'flowspace',
  name: 'Flowspace',
  domain: 'flow.space',
  type: '3PL partner',
  stage: 'Contract sent',
  owner: 'Rich Amsinger',
  contract: 'Sent',
  value: '—',
  lastActivity: '6d',
  location: 'Los Angeles, US'
}, {
  id: 'poshmark-partner',
  name: 'Poshmark',
  domain: 'poshmark.com',
  type: 'Strategic partner',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '—',
  lastActivity: '1d',
  location: 'Redwood City, US'
}, {
  id: 'whatnot-partner',
  name: 'Whatnot',
  domain: 'whatnot.com',
  type: 'Strategic partner',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '—',
  lastActivity: '1w',
  location: 'Marina del Rey, US'
}, {
  id: 'bloomy',
  name: 'Bloomy Collective',
  domain: 'bloomycollective.com',
  type: 'Portfolio company',
  stage: 'Signed',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '$12,600',
  lastActivity: '2w',
  location: 'Austin, US'
}];
export const initialActivity: Activity[] = [{
  id: 'a1',
  company: 'Eddy',
  kind: 'Email',
  summary: 'Sent Spring Collection recovery report and nine-month plan.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 9:42 AM'
}, {
  id: 'a2',
  company: 'SEAESTA SURF',
  kind: 'Contract',
  summary: 'Recovery agreement sent for signature.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 8:15 AM'
}, {
  id: 'a3',
  company: 'Nature Baby',
  kind: 'Note',
  summary: 'Unit counts on summer submission need confirmation.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 6:58 AM'
}, {
  id: 'a4',
  company: 'Lili Blanc',
  kind: 'Email',
  summary: 'Follow-up on excess inventory volume for Q3.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 4:20 PM'
}, {
  id: 'a5',
  company: 'MATDALEE Studio',
  kind: 'Contract',
  summary: 'Agreement countersigned — ready for onboarding.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 11:02 AM'
}, {
  id: 'a6',
  company: 'Flowspace',
  kind: 'Call',
  summary: 'Discussed intake SLAs and returns processing fees.',
  owner: 'Rich Amsinger',
  timestamp: 'Monday, 2:30 PM'
}];
const suggestedPrompts = ['Which brands have signed but not activated?', 'Email Lili Blanc about more excess inventory', 'Add a new company', 'What contracts are waiting on signature?'];
export function PipelineDashboard() {
  const [companies, setCompanies] = useState(initialCompanies);
  const [activity, setActivity] = useState(initialActivity);
  const [view, setView] = useState<'board' | 'companies' | 'activity'>('board');
  const [typeFilter, setTypeFilter] = useState<CompanyType | 'All'>('All');
  const [query, setQuery] = useState('');
  const [prompt, setPrompt] = useState('');
  const [chat, setChat] = useState<ChatEntry[]>([{
    id: 1,
    author: 'carli',
    text: 'Good morning, Rich. There are 4 companies signed but not yet activated, 3 contracts waiting on signature, and 2 leads with no activity in over a week. What would you like to work on?',
    citation: 'Pipeline · Contracts · Activity'
  }]);
  const [createOpen, setCreateOpen] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    domain: '',
    type: 'Brand' as CompanyType,
    owner: 'Meris Butler',
    location: '',
    value: ''
  });
  const chatIdRef = useRef(2);
  const filteredCompanies = useMemo(() => companies.filter(company => {
    const matchesType = typeFilter === 'All' || company.type === typeFilter;
    const matchesQuery = company.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
    return matchesType && matchesQuery;
  }), [companies, typeFilter, query]);
  const signedNotActivated = companies.filter(company => company.stage === 'Signed');
  const contractsOut = companies.filter(company => company.contract === 'Sent');
  const applyIntent = (text: string) => {
    const lower = text.toLocaleLowerCase();
    if ((lower.includes('add') || lower.includes('create')) && lower.includes('compan')) {
      setCreateOpen(true);
      return 'Pipeline · Companies';
    }
    if (lower.includes('signed') && (lower.includes('not activated') || lower.includes("hasn't activated") || lower.includes('no activation'))) {
      setView('companies');
      setTypeFilter('All');
      return 'Pipeline · Contract status';
    }
    if (lower.includes('contract')) {
      setView('activity');
      return 'Contracts · Last activity';
    }
    if (lower.includes('email')) {
      const matched = companies.find(company => lower.includes(company.name.toLocaleLowerCase()));
      const target = matched?.name ?? 'the account';
      setActivity(current => [{
        id: `draft-${Date.now()}`,
        company: target,
        kind: 'Email',
        summary: 'Carli drafted an outreach email for review.',
        owner: 'Carli · pending review',
        timestamp: 'Just now'
      }, ...current]);
      return 'Activity · Email draft';
    }
    return 'Pipeline assistant';
  };
  const pipelineContext = () => ['You are the sales pipeline assistant for the Manymoons revenue team. The user is a sales rep working their book of business.', 'You can pull lists, summarize pipeline health, draft outreach emails for approval, and create company records. Any email you draft is logged for human approval before it sends — say so.', `Pipeline (${companies.length} companies), formatted as name | category | stage | contract | owner | opportunity | last activity:`, ...companies.map(company => `- ${company.name} | ${company.type} | ${company.stage} | contract ${company.contract} | ${company.owner} | ${company.value} | last activity ${company.lastActivity}`), '', 'Recent activity log:', ...activity.slice(0, 8).map(item => `- ${item.timestamp} · ${item.company} · ${item.kind} · ${item.summary} (${item.owner})`), '', 'Stages run Lead → Qualified → Contract sent → Signed → Activated. "Signed" means the contract is executed but the brand has not submitted inventory yet.', 'Answer using only the pipeline above. When drafting an email, write the actual subject line and body copy.'].join('\n');
  const runPrompt = async (text: string) => {
    if (thinking) return;
    const citation = applyIntent(text);
    const history: AgentMessage[] = [...chat.map(entry => ({
      role: entry.author === 'user' ? 'user' as const : 'assistant' as const,
      content: entry.text
    })), {
      role: 'user',
      content: text
    }];
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'user',
      text
    }]);
    setPrompt('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: pipelineContext(),
        maxTokens: 900
      });
      setChat(current => [...current, {
        id: chatIdRef.current++,
        author: 'carli',
        text: reply,
        citation
      }]);
    } catch (caught) {
      setChat(current => [...current, {
        id: chatIdRef.current++,
        author: 'carli',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submitPrompt = (event: FormEvent) => {
    event.preventDefault();
    const text = prompt.trim();
    if (text) void runPrompt(text);
  };
  const createCompany = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    setCompanies(current => [{
      id: draft.name.toLocaleLowerCase().replace(/\s+/g, '-'),
      name: draft.name.trim(),
      domain: draft.domain.trim() || `${draft.name.trim().toLocaleLowerCase().replace(/\s+/g, '')}.com`,
      type: draft.type,
      stage: 'Lead',
      owner: draft.owner,
      contract: 'None',
      value: draft.value.trim() || '—',
      lastActivity: 'Just now',
      location: draft.location.trim() || '—'
    }, ...current]);
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'carli',
      text: `${draft.name.trim()} is now in the pipeline as a ${draft.type.toLocaleLowerCase()} at the Lead stage, assigned to ${draft.owner}.`,
      citation: 'Pipeline · Companies'
    }]);
    setDraft({
      name: '',
      domain: '',
      type: 'Brand',
      owner: 'Meris Butler',
      location: '',
      value: ''
    });
    setCreateOpen(false);
    setView('companies');
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="pipeline-heading" data-id="element-1944" data-mp="2e71smwbcp3aq">
      <section className="rounded-3xl border border-[#BB5E40]/20 bg-[#BB5E40]/5 p-5 sm:p-6" aria-labelledby="pipeline-heading" data-id="element-1945" data-mp="2j6epm9vxrqr1">
        <div className="flex items-center justify-between gap-4" data-id="element-1946" data-mp="255bz9kxwruab">
          <div className="flex items-center gap-2" data-id="element-1947" data-mp="2a4oy74220rt9">
            <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
            WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain'
          }} data-id="element-1948" data-mp="2a3djwaazg1nw" />
            <h1 id="pipeline-heading" className="text-sm font-semibold" data-id="element-1949" data-mp="xuhk3argmiay">Ask Carli</h1>
          </div>
          <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1950" data-mp="sw674r5vbroi">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1951" data-mp="12w6r8fbkabtr" />
            Create company
          </button>
        </div>

        <ul className="mt-4 space-y-3" aria-live="polite" data-id="element-1952" data-mp="1bv20y1dte41t">
          {chat.slice(-4).map(entry => <li key={entry.id} className={entry.author === 'user' ? 'flex justify-end' : ''} data-id="element-1953" data-mp="1qjy4pm8u8uwm">
              <div className={`max-w-2xl whitespace-pre-line rounded-2xl px-4 py-3 text-sm leading-relaxed ${entry.author === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/75 shadow-sm'}`} data-id="element-1954" data-mp="1ljsapzf3iwbd">
                {entry.text}
                {entry.citation && <p className="mt-2 text-[11px] uppercase tracking-wider text-ink/40" data-id="element-1955" data-mp="1vkcg9e9nemze">{entry.citation}</p>}
              </div>
            </li>)}
          {thinking && <li className="flex items-center gap-2 text-xs text-ink/50" data-id="element-1956" data-mp="3tz0ek9c7g2ce">
              <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-1957" data-mp="2l47hb9oglvf" />
              Carli is thinking…
            </li>}
        </ul>

        <form onSubmit={submitPrompt} className="mt-4" data-id="element-1958" data-mp="362cmlnd0t7mg">
          <label htmlFor="pipeline-prompt" className="sr-only" data-id="element-1959" data-mp="363xf77frygho">Ask Carli about your pipeline</label>
          <div className="flex items-center gap-2 rounded-2xl border border-[#BB5E40]/30 bg-paper p-2 pl-4 focus-within:border-[#BB5E40] focus-within:ring-2 focus-within:ring-[#BB5E40]/15" data-id="element-1960" data-mp="3b1z6gfnctm48">
            <input id="pipeline-prompt" value={prompt} onChange={event => setPrompt(event.target.value)} placeholder="Ask Carli to pull a list, draft an email, or add a company…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-1961" data-mp="3k2x6kmuo99hj" />
            <button type="submit" disabled={!prompt.trim() || thinking} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#BB5E40] px-3.5 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" data-id="element-1962" data-mp="2frtzt9rmi00j">
              {thinking ? <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-1963" data-mp="214r2p477ptdq" /> : <SendHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1964" data-mp="1w3bkdjwf60z1" />}
              Send
            </button>
          </div>
        </form>

        <div className="mt-3 flex flex-wrap gap-2" data-id="element-1965" data-mp="1s4m49jjzy5bp">
          {suggestedPrompts.map(item => <button key={item} type="button" onClick={() => void runPrompt(item)} className="rounded-full border border-[#BB5E40]/25 bg-paper px-3 py-1.5 text-xs font-medium text-ink/70 transition-colors hover:border-[#BB5E40]/50 hover:text-[#94452E]" data-id="element-1966" data-mp="1n6123el6pbkm">
              {item}
            </button>)}
        </div>
      </section>

      <section className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Pipeline summary" data-id="element-1967" data-mp="3rlmllc9r353r">
        <SummaryCard icon={BuildingIcon} label="Companies" value={`${companies.length}`} detail="Across all categories" data-id="element-1968" data-mp="f3by0fnhl71" />
        <SummaryCard icon={FileSignatureIcon} label="Contracts out" value={`${contractsOut.length}`} detail="Awaiting signature" data-id="element-1969" data-mp="o4nx8pza0ujh" />
        <SummaryCard icon={CheckCircle2Icon} label="Signed, not activated" value={`${signedNotActivated.length}`} detail="Ready for onboarding" data-id="element-1970" data-mp="s5zeo87zy9gl" />
        <SummaryCard icon={ClockIcon} label="Open pipeline" value="$372k" detail="Estimated recovery value" data-id="element-1971" data-mp="j5koovk3866r" />
      </section>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1972" data-mp="2yvxh2oxu0mxe">
        <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-1973" data-mp="33wbny3np5qtu">
          {([['board', 'Board', LayoutGridIcon], ['companies', 'Companies', ListIcon], ['activity', 'Activity', MailIcon]] as const).map(([id, label, Icon]) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === id ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1974" data-mp="2rfk4uhtcn8vq">
              <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1975" data-mp="28o9fw676p66h" />
              {label}
            </button>)}
        </div>

        {view !== 'activity' && <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-1976" data-mp="2hn4m3ia8ag9s">
            <div className="relative" data-id="element-1977" data-mp="1ty29iougbdm5">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1978" data-mp="1tx0kthkiisly" />
              <label htmlFor="pipeline-search" className="sr-only" data-id="element-1979" data-mp="1owwbcbi0uy58">Search companies</label>
              <input id="pipeline-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search companies" className="w-full rounded-xl border border-ink/15 bg-paper py-2.5 pl-8 pr-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-[#BB5E40]/35 sm:w-56" data-id="element-1980" data-mp="14f8f2f6h7j2g" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1981" data-mp="1rd376r5nety8">
              {(['All', ...companyTypes] as const).map(item => <button key={item} type="button" onClick={() => setTypeFilter(item)} aria-pressed={typeFilter === item} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${typeFilter === item ? 'bg-ink text-cream' : 'border border-ink/15 bg-paper text-ink/60 hover:text-ink'}`} data-id="element-1982" data-mp="1w5mmptnwbz5o">
                  {item}
                </button>)}
            </div>
          </div>}
      </div>

      {view === 'board' && <section className="mt-5 -mx-2 overflow-x-auto px-2 pb-2" aria-label="Pipeline board" data-id="element-1983" data-mp="3ll72blv7714p">
          <div className="grid min-w-[900px] grid-cols-5 gap-3" data-id="element-1984" data-mp="3gmm1bk84gdf7">
            {stages.map(stage => {
          const stageCompanies = filteredCompanies.filter(company => company.stage === stage);
          return <div key={stage} className="rounded-2xl border border-ink/10 bg-cream/45 p-3" data-id="element-1985" data-mp="d6opl6ubaxer">
                  <div className="flex items-center justify-between gap-2" data-id="element-1986" data-mp="8dvl8k2r9r37">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink/55" data-id="element-1987" data-mp="3g3xm2958r4t">{stage}</h3>
                    <span className="rounded-full bg-paper px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-1988" data-mp="10vrjpe64uiq5">{stageCompanies.length}</span>
                  </div>
                  <ul className="mt-3 space-y-2" data-id="element-1989" data-mp="w2ol2rmi0j33">
                    {stageCompanies.map(company => <li key={company.id} data-id="element-1990" data-mp="2mmzcckztfp6z">
                        <article className="rounded-xl border border-ink/10 bg-paper p-3" data-id="element-1991" data-mp="2hp7gxgzypky1">
                          <p className="text-sm font-semibold text-ink" data-id="element-1992" data-mp="2dnvs3dowx3qw">{company.name}</p>
                          <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1993" data-mp="28q41qo3x1pot">{company.domain}</p>
                          <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-1994" data-mp="35gcxxzhn2j9x">{company.type}</span>
                          <div className="mt-3 flex items-center justify-between gap-2 border-t border-ink/10 pt-2 text-[11px] text-ink/55" data-id="element-1995" data-mp="31gcicbit021a">
                            <span className="truncate" data-id="element-1996" data-mp="2royexsnqei2v">{company.owner}</span>
                            <span className="shrink-0 font-semibold text-ink" data-id="element-1997" data-mp="3p3u51sn7o9xe">{company.value}</span>
                          </div>
                        </article>
                      </li>)}
                    {stageCompanies.length === 0 && <li className="rounded-xl border border-dashed border-ink/15 px-2 py-6 text-center text-[11px] text-ink/40" data-id="element-1998" data-mp="3rpk0kn2xlxv1">No companies</li>}
                  </ul>
                </div>;
        })}
          </div>
        </section>}

      {view === 'companies' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Companies" data-id="element-1999" data-mp="9jotn9sryrvw">
          <div className="-mx-2 overflow-x-auto px-2" data-id="element-2000" data-mp="eh7454mfxf60">
            <table className="w-full min-w-[820px] text-left text-xs" data-id="element-2001" data-mp="jg9zfxcmc70s">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-2002" data-mp="jegssi209mss">
                <tr data-id="element-2003" data-mp="o81t4gr2msv5">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2004" data-mp="o9w3j14pv2kz">Company</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2005" data-mp="t638orpaga1v">Category</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2006" data-mp="x99ejy8hnz7t">Stage</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2007" data-mp="126rpfu62goig">Contract</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2008" data-mp="2yafo9wl7aas9">Owner</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2009" data-mp="2yapi5v9d9238">Opportunity</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2010" data-mp="2uap1e3ysoet0">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-2011" data-mp="339iv9mkqqr2v">
                {filteredCompanies.map(company => <tr key={company.id} data-id="element-2012" data-mp="3hxx5wjoefjtz">
                    <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-2013" data-mp="3hwvjxk5uhcxh">
                      <span className="flex items-center gap-2.5" data-id="element-2014" data-mp="3cyk9b77q6ye7">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cream text-[10px] font-bold text-ink/60" data-id="element-2015" data-mp="3qzmvdjyq08p4">{company.name.slice(0, 2).toLocaleUpperCase()}</span>
                        <span className="min-w-0" data-id="element-2016" data-mp="1wkkci546fov6">
                          <span className="block truncate" data-id="element-2017" data-mp="1wlc4z72h96h8">{company.name}</span>
                          <span className="block truncate text-[11px] font-normal text-ink/45" data-id="element-2018" data-mp="1rnaman8yr96a">{company.location}</span>
                        </span>
                      </span>
                    </th>
                    <td className="py-3 pr-3" data-id="element-2019" data-mp="c2jla5naeg1t"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-2020" data-mp="c55ue9ptt3m5">{company.type}</span></td>
                    <td className="py-3 pr-3" data-id="element-2021" data-mp="2cgijdqd5nqa"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stageTone[company.stage]}`} data-id="element-2022" data-mp="2bx80i31m1ma">{company.stage}</span></td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-2023" data-mp="3phy1wy8iy5x6">
                      <span className="inline-flex items-center gap-1.5" data-id="element-2024" data-mp="3pjiuwjf6xgsx">
                        {company.contract === 'Signed' ? <CheckCircle2Icon className="h-3.5 w-3.5 text-forest" aria-hidden="true" data-id="element-2025" data-mp="3bjzlzoek21fk" /> : company.contract === 'Sent' ? <FileTextIcon className="h-3.5 w-3.5 text-[#C9A04E]" aria-hidden="true" data-id="element-2026" data-mp="3fluo2v4b8iue" /> : <span className="h-1.5 w-1.5 rounded-full bg-ink/20" aria-hidden="true" data-id="element-2027" data-mp="19rbh7am9j5xo" />}
                        {company.contract}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-2028" data-mp="p0g1mmp53jlz">{company.owner}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-2029" data-mp="k2eiy2vmlmb1">{company.value}</td>
                    <td className="py-3 text-right whitespace-nowrap text-ink/55" data-id="element-2030" data-mp="f2jqvw046qoe">{company.lastActivity}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredCompanies.length === 0 && <p className="py-8 text-center text-sm text-ink/55" data-id="element-2031" data-mp="64zmie6yge9k">No companies match this filter.</p>}
        </section>}

      {view === 'activity' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Activity log" data-id="element-2032" data-mp="3sjj1abzudvgk">
          <h2 className="text-base font-semibold" data-id="element-2033" data-mp="3nky1gdoi5dsn">Recent activity</h2>
          <p className="mt-1 text-xs text-ink/55" data-id="element-2034" data-mp="3ikjtevmwi3um">Emails, contracts, calls, and notes across every account.</p>
          <ol className="mt-5 space-y-2" data-id="element-2035" data-mp="3ejztxleuuo5b">
            {activity.map(item => <li key={item.id} className="flex gap-3 rounded-xl border border-ink/10 bg-cream/40 p-4" data-id="element-2036" data-mp="1u1fifykueeur">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-paper" data-id="element-2037" data-mp="1y17w2pp3fb6z">
                  {item.kind === 'Email' ? <MailIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2038" data-mp="2325ff5luaba2" /> : item.kind === 'Contract' ? <FileSignatureIcon className="h-4 w-4 text-[#C9A04E]" aria-hidden="true" data-id="element-2039" data-mp="27znn6so4ekgh" /> : item.kind === 'Call' ? <PhoneIcon className="h-4 w-4 text-[#4D9CC7]" aria-hidden="true" data-id="element-2040" data-mp="1aehvlsse7ooh" /> : <UserRoundIcon className="h-4 w-4 text-ink/45" aria-hidden="true" data-id="element-2041" data-mp="1fct5g3iotz6p" />}
                </span>
                <div className="min-w-0" data-id="element-2042" data-mp="1ka2u3gy966th">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-2043" data-mp="1p1kt3zgptxbt">
                    <p className="text-sm font-semibold text-ink" data-id="element-2044" data-mp="1p2vvpvyfcz1c">{item.company}</p>
                    <span className="rounded-full bg-ink/7 px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-2045" data-mp="rle001vm71r8">{item.kind}</span>
                    <span className="text-[11px] text-ink/45" data-id="element-2046" data-mp="wj73gn5j9hnt">{item.timestamp}</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-2047" data-mp="oi7w0xrk0wpx">{item.summary}</p>
                  <p className="mt-1 text-[11px] text-ink/45" data-id="element-2048" data-mp="kgwab3bswvn0">{item.owner}</p>
                </div>
              </li>)}
          </ol>
        </section>}

      {createOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="create-company-heading" data-id="element-2049" data-mp="tddoygjdjbni">
          <button type="button" className="absolute inset-0 cursor-default" onClick={() => setCreateOpen(false)} aria-label="Close create company" data-id="element-2050" data-mp="1lxu5z7nyim7" />
          <form onSubmit={createCompany} className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-paper p-6 shadow-2xl" data-id="element-2051" data-mp="ak9tqqqicqqs">
            <div className="flex items-start justify-between gap-4" data-id="element-2052" data-mp="1qxrougomktui">
              <div data-id="element-2053" data-mp="1m0hr8dr31bvl">
                <h2 id="create-company-heading" className="text-lg font-semibold" data-id="element-2054" data-mp="1lz6jy3yfhlzq">Create company</h2>
                <p className="mt-1 text-xs text-ink/55" data-id="element-2055" data-mp="33wthakvziw4e">Carli files new records at the Lead stage.</p>
              </div>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close" data-id="element-2056" data-mp="38xzd4rkxyzi5"><XIcon className="h-4 w-4" data-id="element-2057" data-mp="2zvhn02261mpv" /></button>
            </div>

            <div className="mt-5 space-y-3" data-id="element-2058" data-mp="2zxuf381n1dgn">
              <Field label="Company name" value={draft.name} onChange={value => setDraft(current => ({
            ...current,
            name: value
          }))} required data-id="element-2059" data-mp="3nmn2uh83wf06" />
              <Field label="Domain" value={draft.domain} onChange={value => setDraft(current => ({
            ...current,
            domain: value
          }))} placeholder="brand.com" data-id="element-2060" data-mp="3rpa2h4b0r47x" />
              <div data-id="element-2061" data-mp="3itu0u140vkrs">
                <label htmlFor="company-type" className="text-xs font-semibold text-ink/60" data-id="element-2062" data-mp="3it10bhvnpri3">Category</label>
                <select id="company-type" value={draft.type} onChange={event => setDraft(current => ({
              ...current,
              type: event.target.value as CompanyType
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-2063" data-mp="acz5d6pk12nq">
                  {companyTypes.map(item => <option key={item} value={item} data-id="element-2064" data-mp="21grdgbl04tzx">{item}</option>)}
                </select>
              </div>
              <div data-id="element-2065" data-mp="2u7j28nzqmgd8">
                <label htmlFor="company-owner" className="text-xs font-semibold text-ink/60" data-id="element-2066" data-mp="2u816o7wk00w8">Owner</label>
                <select id="company-owner" value={draft.owner} onChange={event => setDraft(current => ({
              ...current,
              owner: event.target.value
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-2067" data-mp="2p8eqpqpcc6jq">
                  {['Meris Butler', 'Meg Sawtelle', 'Rich Amsinger'].map(item => <option key={item} value={item} data-id="element-2068" data-mp="14uxpzzm0k7xn">{item}</option>)}
                </select>
              </div>
              <Field label="Location" value={draft.location} onChange={value => setDraft(current => ({
            ...current,
            location: value
          }))} placeholder="City, Country" data-id="element-2069" data-mp="3gedfidcy0ib9" />
              <Field label="Estimated opportunity" value={draft.value} onChange={value => setDraft(current => ({
            ...current,
            value
          }))} placeholder="$25,000" data-id="element-2070" data-mp="3pfbm93ern3xj" />
            </div>

            <div className="mt-6 flex gap-2" data-id="element-2071" data-mp="308qbr41z2dk">
              <button type="submit" className="flex-1 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-2072" data-mp="33moen5oopf7">Create company</button>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink/65 hover:bg-cream" data-id="element-2073" data-mp="bzvps7d81y3z">Cancel</button>
            </div>
          </form>
        </div>}
    </div>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  required
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const id = label.toLocaleLowerCase().replace(/\s+/g, '-');
  return <div data-id="element-2074" data-mp="296ra0w0zoav4">
      <label htmlFor={id} className="text-xs font-semibold text-ink/60" data-id="element-2075" data-mp="1ugbqrms4vczl">{label}</label>
      <input id={id} value={value} required={required} placeholder={placeholder} onChange={event => onChange(event.target.value)} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink/35 focus:border-[#BB5E40]/35" data-id="element-2076" data-mp="1ue7fukvthdpt" />
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof BuildingIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-2077" data-mp="12p47ou0t7j7w">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2078" data-mp="12qywijf7bvh9" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-2079" data-mp="16r8xtis1txps">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2080" data-mp="z6olfrxx8vao">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-2081" data-mp="u83xaqvy222l">{detail}</p>
    </article>;
}